import { api } from "./client";
import type { CaptureBundleResult, CocoImportReport, FolderList, ImageOut } from "./types";

/** Stay well under the server's MAX_FILES_PER_UPLOAD (frontend/api_client.py UPLOAD_BATCH_SIZE). */
export const UPLOAD_BATCH_SIZE = 100;

export interface ImageFilters { status?: string | null; review?: string | null; folder?: string | null; augmented?: "only" | "exclude" | null;
  /** Only images holding a reviewed annotation of this class. */
  class_id?: number | null }

/** folder: undefined/null = all folders; "" = images with no folder (the server distinguishes the two). */
export const listImages = (pid: number, f: ImageFilters = {}) =>
  api.get<ImageOut[]>(`/projects/${pid}/images`, {
    query: { folder: f.folder === null ? undefined : f.folder, status: f.status || undefined, review: f.review || undefined, augmented: f.augmented || undefined,
      class_id: f.class_id ?? undefined },
  });

export const listFolders = (pid: number) => api.get<FolderList>(`/projects/${pid}/folders`);
export const deleteImage = (id: number) => api.del<void>(`/images/${id}`);
/** Put images into one folder; "" takes them out of every folder. */
export const moveImages = (pid: number, imageIds: number[], folder: string) =>
  api.post<{ folder: string | null; images_requested: number; images_not_found: number; images_moved: number }>(
    `/projects/${pid}/images/move`, { json: { image_ids: imageIds, folder } });

/** Split into server-sized batches, preserving order. */
export function batches<T>(items: T[], size = UPLOAD_BATCH_SIZE): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Upload in batches; `onProgress(done, total)` after each batch. */
export async function uploadImages(
  pid: number, files: File[], opts: { folder?: string | null; onProgress?: (done: number, total: number) => void } = {},
): Promise<ImageOut[]> {
  const results: ImageOut[] = [];
  let done = 0;
  for (const batch of batches(files)) {
    const form = new FormData();
    for (const f of batch) form.append("files", f, f.name);
    if (opts.folder) form.append("folder", opts.folder);
    results.push(...(await api.post<ImageOut[]>(`/projects/${pid}/images`, { form })));
    done += batch.length;
    opts.onProgress?.(done, files.length);
  }
  return results;
}

export function uploadCaptures(pid: number, files: File[]): Promise<ImageOut[]> {
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  return api.post<ImageOut[]>(`/projects/${pid}/captures`, { form });
}

export function importCoco(pid: number, file: File, mode: "merge" | "replace"): Promise<CocoImportReport> {
  const form = new FormData();
  form.append("file", file, file.name);
  return api.post<CocoImportReport>(`/projects/${pid}/import`, { form, query: { mode } });
}

/**
 * Throws ModelImportRefused (409) when the bundle names classes the project lacks. `folder` files every capture in that
 * one folder ("" for none); without it they go to ctrlx/<device>/cam<N>, as the desktop asks.
 */
export function importCaptureBundle(
  pid: number, file: File, device: string, createClasses = false, classMapping?: Record<string, string>, folder?: string,
): Promise<CaptureBundleResult> {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("device", device);
  form.append("create_classes", String(createClasses));
  if (folder !== undefined) { form.append("into_folder", "true"); form.append("folder", folder); }
  if (classMapping && Object.keys(classMapping).length) form.append("class_mapping", JSON.stringify(classMapping));
  return api.post<CaptureBundleResult>(`/projects/${pid}/images/import-captures`, { form });
}

export const thumbnailBlob = (id: number, signal?: AbortSignal) =>
  api.get<Blob>(`/images/${id}/thumbnail`, { as: "blob", signal });
